/**
 * Faixa discreta no topo do conteúdo quando o certificado digital do usuário está
 * a 7 dias ou menos do vencimento, ou já venceu (docs/PLANO_CERTIFICADO_EXPIRADO.md).
 *
 * Antes de 7 dias o aviso é só o e-mail (30 e 7 dias): uma faixa por um mês
 * inteiro vira ruído que ninguém lê. Fonte: `certificate_valid_until` do
 * `/auth/me`, atualizado pelo `refreshUser()` do upload — enviar o renovado tira a
 * faixa sem recarregar a página.
 *
 * Dispensável só na sessão atual (sessionStorage, por usuário): volta na próxima aba.
 */
import { ShieldAlert, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useAtuacao } from '@/lib/delegacao/atuacao';
import { cn } from '@/lib/utils';

const DIAS_FAIXA = 7;
const MS_POR_DIA = 24 * 60 * 60 * 1000;
const ROTA_CERTIFICADOS = '/app/certificados';

const formatarData = (data: Date) =>
  data.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });

/** Dia de calendário em Brasília como número de dias desde a época (para subtrair). */
const diaEmBrasilia = (ms: number) => {
  const [d, m, a] = formatarData(new Date(ms)).split('/').map(Number);
  return Date.UTC(a, m - 1, d) / MS_POR_DIA;
};

/** `null` = sem faixa. Exportado para teste. */
export function situacaoVencimento(
  validoAte: string | null | undefined,
  agora: number = Date.now(),
): { vencido: boolean; texto: string } | null {
  if (!validoAte) return null;
  const fim = new Date(validoAte);
  if (Number.isNaN(fim.getTime())) return null;
  const restanteMs = fim.getTime() - agora;
  if (restanteMs <= 0) {
    return { vencido: true, texto: `Seu certificado venceu em ${formatarData(fim)}.` };
  }
  // Dias de calendário, não períodos de 24 h: "vence amanhã (18/10)" no próprio dia 18 contradiz a data.
  const dias = diaEmBrasilia(fim.getTime()) - diaEmBrasilia(agora);
  if (dias > DIAS_FAIXA) return null;
  const quando = dias === 0 ? 'hoje' : dias === 1 ? 'amanhã' : `em ${dias} dias`;
  return { vencido: false, texto: `Seu certificado vence ${quando} (${formatarData(fim)}).` };
}

export function CertificadoVencimentoFaixa() {
  const { user } = useAuth();
  const atuacao = useAtuacao();
  const { pathname } = useLocation();
  const situacao = situacaoVencimento(user?.certificate_valid_until);
  const chaveDispensa = `certificado:faixaDispensada:${user?.id ?? ''}`;
  const [dispensadaAgora, setDispensadaAgora] = useState<string | null>(null);
  let dispensada = dispensadaAgora === chaveDispensa;
  try {
    dispensada ||= sessionStorage.getItem(chaveDispensa) === '1';
  } catch {
    /* sessionStorage indisponível — vale só o estado em memória */
  }
  const dispensar = () => {
    setDispensadaAgora(chaveDispensa);
    try {
      sessionStorage.setItem(chaveDispensa, '1');
    } catch {
      /* sem persistência — dispensa vale até a página recarregar */
    }
  };

  // Na conta de outra pessoa vale o certificado do titular, não o seu; e na própria
  // tela de Certificados a faixa só repete o que a lista já mostra.
  if (atuacao || pathname.startsWith(ROTA_CERTIFICADOS)) return null;
  if (!situacao || dispensada) return null;

  return (
    <div
      role="status"
      className={cn(
        'mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md px-3 py-2 text-sm ring-1',
        situacao.vencido
          ? 'bg-[var(--color-error-bg)] text-[var(--color-error)] ring-[var(--color-error)]/30'
          : 'bg-[var(--color-warning-bg)] text-[var(--color-warning)] ring-[var(--color-warning)]/30',
      )}
    >
      <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1">{situacao.texto}</span>
      <Button asChild variant="link" size="sm" className="h-auto min-h-[44px] px-0 text-inherit underline sm:min-h-0">
        <Link to={ROTA_CERTIFICADOS}>{situacao.vencido ? 'Enviar o renovado' : 'Renovar'}</Link>
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-11 w-11 shrink-0 text-inherit hover:bg-transparent hover:opacity-70 sm:h-7 sm:w-7"
        onClick={dispensar}
        aria-label="Dispensar aviso do certificado"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  );
}
